import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable, shareReplay } from 'rxjs';

import { environment } from 'environments/environment';
import { Pageable } from 'app/models/Pageable.model';

const { apiUrl } = environment;

export interface AppConfig {
  logoGeneralUrl: string | null;
  logoSinTextoUrl: string | null;
  faviconUrl: string | null;
  logoTurnoUrl: string | null;
  iconoAvisosUrl: string | null;
  telefonoEmergencia: string | null;
  telefonoAtencion: string | null;
  tamanoPassword: number | null;
  requiereCaracteresEspeciales: boolean | null;
  requiereMayusculas: boolean | null;
  requiereNumeros: boolean | null;
  maxPeticionesBloqueoWeb: number | null;
  maxPeticionesBloqueoWhats: number | null;
  mensajeIntroTitulo: string | null;
  mensajeIntroDescripcion: string | null;
}

export interface PasswordPolicy {
  tamanoPassword: number | null;
  requiereCaracteresEspeciales: boolean | null;
  requiereMayusculas: boolean | null;
  requiereNumeros: boolean | null;
}

export type HistorialConfigApp = {
  id: number;
  usuarioId: number | null;
  usuarioNombre: string | null;
  usuarioEmail: string | null;
  configuracionAnterior: AppConfig | null;
  configuracionNueva: AppConfig;
  fecha: string;
};

export type HistorialConfigAppFiltro = {
  usuarioId?: number;
  fechaInicio?: string;
  fechaFin?: string;
  sortBy?: string;
  sortDirection?: string;
  page?: number;
  size?: number;
};

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  private http = inject(HttpClient);

  private config$: Observable<AppConfig> = this.http
    .get<AppConfig>(`${apiUrl}/configs/app`)
    .pipe(shareReplay(1));

  getConfig(): Observable<AppConfig> {
    return this.config$;
  }

  actualizarConfig(config: AppConfig): Promise<AppConfig> {
    return firstValueFrom(this.http.put<AppConfig>(`${apiUrl}/configs/app`, config));
  }

  subirLogoGeneral(file: File): Promise<AppConfig> {
    return this.subir('logo-general', file);
  }

  subirLogoSinTexto(file: File): Promise<AppConfig> {
    return this.subir('logo-sin-texto', file);
  }

  subirLogoTurno(file: File): Promise<AppConfig> {
    return this.subir('logo-turno', file);
  }

  subirFavicon(file: File): Promise<AppConfig> {
    return this.subir('favicon', file);
  }

  subirIconoAvisos(file: File): Promise<AppConfig> {
    return this.subir('icono-avisos', file);
  }

  private subir(endpoint: string, file: File): Promise<AppConfig> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return firstValueFrom(this.http.post<AppConfig>(`${apiUrl}/configs/app/${endpoint}`, formData));
  }

  getHistorial(filtro: HistorialConfigAppFiltro = {}): Observable<Pageable<HistorialConfigApp>> {
    return this.http.get<Pageable<HistorialConfigApp>>(`${apiUrl}/configs/app/historial`, {
      params: this.construirParamsHistorial(filtro)
    });
  }

  private construirParamsHistorial(filtro: HistorialConfigAppFiltro): Record<string, string | number> {
    const { page = 0, size = 20 } = filtro;
    const params: Record<string, string | number> = { page, size };
    if (filtro.usuarioId != null) params.usuarioId = filtro.usuarioId;
    if (filtro.fechaInicio) params.fechaInicio = filtro.fechaInicio;
    if (filtro.fechaFin) params.fechaFin = filtro.fechaFin;
    if (filtro.sortBy) params.sortBy = filtro.sortBy;
    if (filtro.sortDirection) params.sortDir = filtro.sortDirection;
    return params;
  }
}
